import { useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { resolveId } from "@medplum/core";
import { Document, useMedplum } from "@medplum/react";

import { Paper, ScrollArea, Stack, Tabs } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";

import { withProjectAdminGuard } from "./withProjectAdminGuard";

const isDevelopment = import.meta.env.VITE_ENV === "DEVELOPMENT";
const isLocalMedplum =
  import.meta.env.VITE_MEDPLUM_URL?.startsWith("http://localhost") ||
  import.meta.env.VITE_MEDPLUM_URL?.startsWith("http://host.docker.internal");

const TABS = [
  "Details",
  "Setup",
  "Invite Provider",
  "Lab Accounts",
  "Feature Flags",
  "Onboard Practice",
  "Access Reports",
  "Practitioner Roles",
];

if (isDevelopment && isLocalMedplum) {
  TABS.push("Questionnaire");
}

function ProjectContent(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname.replace("/admin/project/", "") || TABS[0];
  const medplum = useMedplum();
  const projectId = resolveId(medplum.getActiveLogin()?.project) as string;
  const result = useMemo(
    () => medplum.get("admin/projects/" + projectId).read(),
    [medplum, projectId],
  );

  /**
   * Handles a tab change event.
   * @param newTabName - The new tab name.
   */
  function onTabChange(newTabName: string | null): void {
    navigate(`/admin/project/${newTabName}`);
  }

  return (
    <>
      <Paper p="md">
        <Stack gap={0}>
          <TextWrapper size="xs" c="gray.6" tt="uppercase">
            Project
          </TextWrapper>
          <TextWrapper variant="bold">{result.project.name}</TextWrapper>
        </Stack>

        <ScrollArea>
          <Tabs onChange={onTabChange} value={currentTab.toLowerCase().replaceAll(" ", "-")}>
            <Tabs.List style={{ whiteSpace: "nowrap", flexWrap: "nowrap" }}>
              {TABS.map((t) => (
                <Tabs.Tab key={t} value={t.toLowerCase().replaceAll(" ", "-")}>
                  {t}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        </ScrollArea>
      </Paper>
      <Document p="md">
        <Outlet />
      </Document>
    </>
  );
}

export const ProjectPage = withProjectAdminGuard(ProjectContent);
